/* eslint-disable no-magic-numbers */
/**
 * Color conversion utilities
 * Extracted from color-convert library to reduce dependencies
 * Based on standard color science algorithms
 */

/**
 * Convert hex color string to RGB array
 * @param hex - Hex color string (e.g., "#FF0000" or "FF0000")
 * @returns RGB array [r, g, b] with values 0-255
 */
export function hexToRgb(hex: string): [number, number, number] {
  // Remove # if present
  const cleanHex = hex.replace(/^#/, '');

  // Handle 3-digit hex
  if (cleanHex.length === 3) {
    const r = parseInt(cleanHex[0] + cleanHex[0], 16);
    const g = parseInt(cleanHex[1] + cleanHex[1], 16);
    const b = parseInt(cleanHex[2] + cleanHex[2], 16);
    return [r, g, b];
  }

  // Handle 6-digit hex
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  return [r, g, b];
}

/**
 * Convert RGB to XYZ color space
 * @param rgb - RGB array [r, g, b] with values 0-255
 * @returns XYZ array [x, y, z]
 */
function rgbToXyz(rgb: [number, number, number]): [number, number, number] {
  let [r, g, b] = rgb;

  // Normalize RGB values to 0-1
  r = r / 255;
  g = g / 255;
  b = b / 255;

  // Apply gamma correction
  r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
  g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
  b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;

  // Convert to XYZ using sRGB matrix
  const x = (r * 0.4124564 + g * 0.3575761 + b * 0.1804375) * 100;
  const y = (r * 0.2126729 + g * 0.7151522 + b * 0.072175) * 100;
  const z = (r * 0.0193339 + g * 0.119192 + b * 0.9503041) * 100;

  return [x, y, z];
}

/**
 * Convert XYZ to LAB color space
 * @param xyz - XYZ array [x, y, z]
 * @returns LAB array [L, a, b]
 */
function xyzToLab(xyz: [number, number, number]): [number, number, number] {
  const [x, y, z] = xyz;

  // D65 illuminant (standard daylight)
  const xn = 95.047;
  const yn = 100.0;
  const zn = 108.883;

  // Normalize by reference white
  let fx = x / xn;
  let fy = y / yn;
  let fz = z / zn;

  // Apply f(t) function
  fx = fx > 0.008856 ? Math.pow(fx, 1 / 3) : 7.787 * fx + 16 / 116;
  fy = fy > 0.008856 ? Math.pow(fy, 1 / 3) : 7.787 * fy + 16 / 116;
  fz = fz > 0.008856 ? Math.pow(fz, 1 / 3) : 7.787 * fz + 16 / 116;

  const L = 116 * fy - 16;
  const a = 500 * (fx - fy);
  const b = 200 * (fy - fz);

  return [L, a, b];
}

/**
 * Convert RGB array to LAB color space
 * @param rgb - RGB array [r, g, b] with values 0-255
 * @returns LAB array [L, a, b]
 */
export function rgbToLab(
  rgb: [number, number, number]
): [number, number, number] {
  const xyz = rgbToXyz(rgb);
  return xyzToLab(xyz);
}

/**
 * Convert RGB array to hex color string
 * @param rgb - RGB array [r, g, b] with values 0-255
 * @returns Hex color string (e.g., "#ff0000")
 */
export function rgbToHex(rgb: [number, number, number]): string {
  const [r, g, b] = rgb;
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Color conversion object matching color-convert API
 */
export const convert = {
  hex: {
    rgb: hexToRgb,
  },
  rgb: {
    lab: rgbToLab,
  },
};
